import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, ShieldOff, AlertTriangle } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { CoverageGuardRuleEditor } from "@/components/CoverageGuardRuleEditor";
import { ruleUsesUnsupportedV1Fields } from "@/lib/coverageGuard";

// Phase 2Z.n — Coverage Guard V1 settings drawer.
//
// Right-side drawer mounted from Rota Style + Roster surfaces. Lists
// the company's Coverage Guard rules and lets admins create / edit /
// archive them via the slim editor. Rules that use unsupported V1
// fields (max_*, time-of-day window, location, apply_per_team, etc.)
// show with a small inline notice — they can still be edited but
// only the V1 fields are exposed; the unsupported fields stay
// untouched on save.

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CoverageGuardDrawer({
  open, onOpenChange, categories, teams, rules, onChanged,
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setEditing(null);
      setEditorOpen(false);
    }
  }, [open]);

  const archive = async (rule) => {
    if (!window.confirm(`Archive "${rule.name}"? You can re-activate it later.`)) {
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/coverage-rules/${rule.coverage_id}`,
        { is_active: false });
      toast.success("Coverage Guard rule archived.");
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail)
        || "Failed to archive Coverage Guard rule.");
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async (rule) => {
    setBusy(true);
    try {
      await api.patch(`/coverage-rules/${rule.coverage_id}`,
        { is_active: true });
      toast.success("Coverage Guard rule re-activated.");
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail)
        || "Failed to re-activate Coverage Guard rule.");
    } finally {
      setBusy(false);
    }
  };

  const renderRule = (r) => {
    const isAdvanced = ruleUsesUnsupportedV1Fields(r);
    const teamName = r.apply_per_team
      ? "Each team (separately)"
      : (r.team_id
          ? (teams.find((t) => t.team_id === r.team_id)?.name || r.team_id)
          : "All teams");
    const catName = categories.find(
      (c) => c.category_id === r.category_id)?.name || r.category_id;
    const dayLabel = (r.days_of_week && r.days_of_week.length > 0)
      ? r.days_of_week.map((d) => DAYS[d]).join(", ")
      : "Every day";
    return (
      <div
        key={r.coverage_id}
        data-testid={`cg-rule-row-${r.coverage_id}`}
        className={`rounded-md border ${
          r.is_active
            ? "border-slate-200 bg-white"
            : "border-slate-200 bg-slate-50 opacity-70"
        } px-3 py-2.5`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">
              {r.name}
              {!r.is_active && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                  Archived
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {catName} · {teamName} · {dayLabel}
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              {r.min_headcount != null && (
                <span>Min {r.min_headcount} {r.min_headcount === 1 ? "person" : "people"}</span>
              )}
              {r.min_headcount != null && r.min_coverage_pct != null && (
                <span> · </span>
              )}
              {r.min_coverage_pct != null && (
                <span>Min {r.min_coverage_pct}% of scheduled minutes</span>
              )}
            </div>
            {/* CG time-window Phase 1 - show which coverage check the
                rule uses. Unflagged rules (even with legacy times)
                are day-level, exactly as today. */}
            <div
              className="text-[11px] text-slate-600 mt-0.5"
              data-testid={`cg-rule-coverage-check-${r.coverage_id}`}
            >
              {(r.coverage_period === "time_window" && r.start_time && r.end_time)
                ? `Specific time window: ${r.start_time} to ${r.end_time}`
                : "Day-level cover"}
            </div>
            {isAdvanced && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                <AlertTriangle className="h-3 w-3" />
                <span>Advanced fields. Coverage Guard treats this as &ldquo;not fully checked&rdquo;</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost" size="sm"
              data-testid={`cg-rule-edit-${r.coverage_id}`}
              onClick={() => { setEditing(r); setEditorOpen(true); }}
              disabled={busy}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {r.is_active ? (
              <Button
                variant="ghost" size="sm"
                data-testid={`cg-rule-archive-${r.coverage_id}`}
                onClick={() => archive(r)}
                disabled={busy}
              >
                <ShieldOff className="h-3.5 w-3.5 text-slate-400" />
              </Button>
            ) : (
              <Button
                variant="ghost" size="sm"
                data-testid={`cg-rule-reactivate-${r.coverage_id}`}
                onClick={() => reactivate(r)}
                disabled={busy}
              >
                Reactivate
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const activeRules = (rules || []).filter((r) => r.is_active);
  const archivedRules = (rules || []).filter((r) => !r.is_active);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[520px] overflow-y-auto"
          data-testid="coverage-guard-drawer"
        >
          <SheetHeader>
            <SheetTitle>Coverage Guard</SheetTitle>
            <SheetDescription>
              Set the minimum number of people needed for a category on selected days. NextRota highlights gaps before the rota is published, so managers can fix under-coverage early.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 mb-3 flex items-center justify-between">
            <div className="text-xs text-slate-500">
              {activeRules.length} active rule{activeRules.length === 1 ? "" : "s"}
            </div>
            <Button
              size="sm"
              data-testid="cg-new-rule-btn"
              onClick={() => { setEditing(null); setEditorOpen(true); }}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              New rule
            </Button>
          </div>
          <div className="space-y-2">
            {activeRules.length === 0 && (
              <div
                className="rounded-md border border-dashed border-slate-200 px-3 py-6 text-center text-sm text-slate-500"
                data-testid="cg-rules-empty"
              >
                No coverage rules yet. Add a rule to help spot under-coverage before publishing.
              </div>
            )}
            {activeRules.map(renderRule)}
            {archivedRules.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-slate-500 cursor-pointer select-none">
                  Archived ({archivedRules.length})
                </summary>
                <div className="space-y-2 mt-2">
                  {archivedRules.map(renderRule)}
                </div>
              </details>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <CoverageGuardRuleEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        editing={editing}
        categories={categories}
        teams={teams}
        onSaved={() => onChanged && onChanged()}
      />
    </>
  );
}

export default CoverageGuardDrawer;
